import { useState, useEffect, useRef, useCallback } from 'react';
import { ReactComponent as SendIcon } from '../assets/svgs/send-icon.svg';
import { ReactComponent as GetQuoteIcon } from '../assets/svgs/get-quote-icon.svg';
import { ReactComponent as TrackShipmentIcon } from '../assets/svgs/track-shipment-icon.svg';
import { ReactComponent as ServicesIcon } from '../assets/svgs/services-offered-icon.svg';
import { ReactComponent as ChatLogo } from '../assets/new-assets/topship-blue-logo.svg';
import { ReactComponent as CloseIcon } from '../assets/svgs/chat-close-icon.svg';
import bgImg from '../assets/images/chat-page-bg.webp';
import { marked } from 'marked';
import { useChatQuery } from '../operations/queries';
import { useRecoilState } from 'recoil';
import { chatWidgetState, carouselMessageState } from '../recoil/atoms';

const ChatWidget = () => {
  const [userMessage, setUserMessage] = useState('');
  const { sendMessage, data, loading } = useChatQuery();
  const [messages, setMessages] = useState([]);
  const [isOpen, setIsOpen] = useRecoilState(chatWidgetState);
  const [carouselMessage, setCarouselMessage] =
    useRecoilState(carouselMessageState);

  const endOfMessagesRef = useRef(null);
  const lastMessageRef = useRef(null);

  const handleSend = async e => {
    e.preventDefault();
    if (loading) return;

    if (!userMessage.trim()) return;

    const userMsg = { role: 'User', message: userMessage };

    setMessages(prev => {
      const updated = [...prev, userMsg];
      sendMessage(userMsg.message, updated);
      return updated;
    });

    setUserMessage('');
  };

  const handlePromptClick = useCallback(
    promptText => {
      const userMsg = { role: 'User', message: promptText };

      setMessages(prev => [...prev, userMsg]);

      sendMessage(promptText, messages);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleTextareaKeyDown = useCallback(e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.target.form.requestSubmit();
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      if (carouselMessage) {
        const userMsg = { role: 'User', message: carouselMessage };

        setMessages(prev => [...prev, userMsg]);

        sendMessage(carouselMessage, messages);
      }
    } else {
      document.body.style.overflow = 'auto';
      setCarouselMessage(null);
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (
      data &&
      data.chat?.message &&
      data.chat?.message !== lastMessageRef.current
    ) {
      const aiMessage = {
        role: 'System',
        message: data.chat.message
      };
      setMessages(prev => [...prev, aiMessage]);
      lastMessageRef.current = data.chat.message;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.chat?.message]);

  useEffect(() => {
    endOfMessagesRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  return (
    <>
      <div
        className={isOpen ? 'chat-widget__background' : ''}
        onClick={() => setIsOpen(false)}
      ></div>
      <div className={`chat-widget ${isOpen ? 'chat-widget--visible' : ''}`}>
        {isOpen && (
          <div className='chat-widget__container'>
            <div className='chat-widget__header'>
              <ChatLogo className='chat-widget__logo' />
              <button
                className='chat-widget__close-btn'
                onClick={() => setIsOpen(false)}
              >
                <CloseIcon />
              </button>
            </div>
            {messages.length < 1 ? (
              <div className='chat__prompt__page'>
                <div className='chat-widget__header'>
                  <ChatLogo className='chat-widget__logo' />
                  <button
                    className='chat-widget__close-btn'
                    onClick={() => setIsOpen(false)}
                  >
                    <CloseIcon />
                  </button>
                </div>
                <p className='chat__prompt__page__text'>
                  How can I make your shipping easier today?
                </p>
                <form
                  className='chat-widget__input-area'
                  onSubmit={e => handleSend(e)}
                >
                  <textarea
                    rows={1}
                    type='text'
                    value={userMessage}
                    onChange={e => setUserMessage(e.target.value)}
                    placeholder='Ask anything'
                    className='chat-widget__input'
                  />
                  <button type='submit' className='chat-widget__send-icon'>
                    <SendIcon />
                  </button>
                </form>
                <div className='chat__prompts'>
                  <div
                    className='chat__prompt'
                    onClick={() =>
                      handlePromptClick(`I'll like to get a quote`)
                    }
                  >
                    <GetQuoteIcon className='chat__prompt__icon' />
                    <p className='chat__prompt__text'>Get a quote</p>
                  </div>
                  <div
                    className='chat__prompt'
                    onClick={() =>
                      handlePromptClick(`I'll like to track my shipment`)
                    }
                  >
                    <TrackShipmentIcon className='chat__prompt__icon' />
                    <p className='chat__prompt__text'>Track shipment</p>
                  </div>
                  <div
                    className='chat__prompt'
                    onClick={() =>
                      handlePromptClick(`What services do you offer?`)
                    }
                  >
                    <ServicesIcon className='chat__prompt__icon' />
                    <p className='chat__prompt__text'>Services offered</p>
                  </div>
                </div>
                <div className='chat__page__bg__box'>
                  <img
                    src={bgImg}
                    alt='background piece'
                    className='chat__page__bg'
                  />
                </div>
              </div>
            ) : (
              <div className='chat-widget__messages'>
                {messages?.map((msg, i) => (
                  <div
                    key={i}
                    className={`chat-widget__message chat-widget__message--${msg.role}`}
                  >
                    <div
                      className={`chat-widget__bubble chat-widget__bubble--${msg.role}`}
                      dangerouslySetInnerHTML={{
                        __html: marked.parse(msg.message)
                      }}
                    />
                  </div>
                ))}
                {loading && (
                  <div className='chat-widget__message chat-widget__message--System'>
                    <div className='chat-widget__bubble chat-widget__bubble--System'>
                      <span className='chat-widget__typing-indicator'>
                        <span></span>
                        <span></span>
                        <span></span>
                      </span>
                    </div>
                  </div>
                )}
                {messages.length > 1 && (
                  <div className='chat__support__prompts'>
                    <a
                      href='https://wa.me/2349080777728'
                      target='_blank'
                      rel='noreferrer'
                      style={{ textDecoration: 'none' }}
                    >
                      <div className='chat__support__prompt'>
                        Whatsapp Support
                      </div>
                    </a>
                    <a
                      href='tel:+2349080777728'
                      target='_blank'
                      rel='noreferrer'
                      style={{ textDecoration: 'none' }}
                    >
                      <div className='chat__support__prompt'>Call Support</div>
                    </a>
                  </div>
                )}
                <div ref={endOfMessagesRef} />
              </div>
            )}
            <form
              className='chat-widget__input-area'
              onSubmit={e => handleSend(e)}
            >
              <textarea
                rows={1}
                type='text'
                value={userMessage}
                onChange={e => setUserMessage(e.target.value)}
                onKeyDown={handleTextareaKeyDown}
                placeholder='Ask anything'
                className='chat-widget__input'
              />
              <button type='submit' className='chat-widget__send-icon'>
                <SendIcon />
              </button>
            </form>
          </div>
        )}
      </div>
    </>
  );
};

export default ChatWidget;
